import { tipoMeta, prioridadMeta, ESTADO_LABEL, AGING_SEG, elapsed, elapsedSeg, CAPACIDAD_LABEL } from "@/lib/alarmMeta";
import { Phone, UserCheck, Wrench } from "lucide-react";

export default function AlarmRow({ alarma, selected, onSelect, nowMs }) {
  const a = alarma;
  const { label: tipoLabel, Icon } = tipoMeta(a.tipo);
  const prio = prioridadMeta(a.prioridad);
  const PrioIcon = prio.Icon;
  const isRecibida = a.estado === "recibida";
  const esTecnica = a.es_tecnica;
  const segs = elapsedSeg(a.hora_recepcion_servidor, nowMs);
  const aging = isRecibida && segs > AGING_SEG;
  const cuidadora = a.cuidadora_en_turno;
  const donde = [a.sitio_nombre, a.zona_nombre].filter(Boolean).join(" · ");

  const tipoHeadline = a.tipo === "cobertura_perdida" && a.capacidad
    ? `${tipoLabel} · ${CAPACIDAD_LABEL[a.capacidad] || a.capacidad}`
    : tipoLabel;

  const base = "w-full text-left rounded-xl border p-4 transition-colors duration-200 focus:outline-none";
  // Técnicas: piel visual propia (ladrillo/gris), NUNCA coral — no deben parecer una alarma clínica.
  // Persona sin tomar: barra coral (indicador de SIN TOMAR).
  let stateCls;
  if (esTecnica) {
    stateCls = isRecibida
      ? "border-l-4 border-l-[#9B2C2C] border-[#9B2C2C]/30 bg-[#9B2C2C]/5 dark:bg-[#9B2C2C]/15 hover:bg-[#9B2C2C]/10"
      : "border-l-4 border-l-slate-400 dark:border-l-slate-500 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700/60";
  } else {
    stateCls = isRecibida
      ? "border-l-4 border-l-[#FE6A55] border-[#FE6A55]/30 bg-[#FE6A55]/10 dark:bg-[#FE6A55]/15 hover:bg-[#FE6A55]/15"
      : "border-l-4 border-l-slate-300 dark:border-l-slate-600 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700/60";
  }
  const selCls = selected ? "ring-2 ring-[#01CBE3] ring-offset-1 dark:ring-offset-slate-900" : "";
  const iconTone = esTecnica
    ? (isRecibida ? "text-[#9B2C2C] dark:text-[#F2B8B8]" : "text-slate-500 dark:text-slate-300")
    : (isRecibida ? "text-[#FE6A55]" : "text-slate-500 dark:text-slate-300");
  const elapsedTone = esTecnica
    ? (isRecibida ? "text-[#9B2C2C] dark:text-[#F2B8B8]" : "text-slate-600 dark:text-slate-300")
    : (isRecibida ? "text-[#FE6A55]" : "text-slate-600 dark:text-slate-300");

  return (
    <button data-testid={`alarm-row-${a.id}`} onClick={() => onSelect(a.id)} className={`${base} ${stateCls} ${selCls}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5 min-w-0">
          <Icon strokeWidth={1.9} className={`h-6 w-6 shrink-0 mt-0.5 ${iconTone}`} />
          <div className="min-w-0">
            {/* Línea 1 — QUÉ ocurrió + prioridad */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-base font-bold uppercase tracking-wide leading-tight text-[#012472] dark:text-slate-100">
                {tipoHeadline}
              </span>
              <span data-testid={`alarm-prioridad-${a.id}`}
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${prio.badge}`}>
                <PrioIcon strokeWidth={2} className="h-3 w-3" /> {prio.label}
              </span>
              {esTecnica && (
                <span className="inline-flex items-center gap-1 rounded-md bg-[#9B2C2C]/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-[#9B2C2C] dark:text-[#F2B8B8]">
                  <Wrench className="h-3 w-3" /> Técnico
                </span>
              )}
            </div>
            {/* Línea 2 — A QUIÉN (solo alarmas de persona) */}
            {!esTecnica && a.persona_nombre && (
              <p className="mt-1 text-base font-medium text-slate-700 dark:text-slate-200 truncate">{a.persona_nombre}</p>
            )}
            {/* Línea 3 — DÓNDE */}
            {donde && <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400 truncate">{donde}</p>}
            {/* Metadata secundaria para técnicas (nunca como título) */}
            {esTecnica && (
              <p className="mt-0.5 text-[11px] text-slate-400 dark:text-slate-500">Incidencia técnica · sin persona asociada</p>
            )}
          </div>
        </div>
        {/* Línea 4 — HACE CUÁNTO / estado */}
        <div className="text-right shrink-0">
          <p data-testid={`alarm-elapsed-${a.id}`}
            className={`tabular-nums text-lg font-bold ${elapsedTone} ${aging ? "animate-pulse" : ""}`}>
            {elapsed(a.hora_recepcion_servidor, nowMs)}
          </p>
          <span className={`mt-1 inline-block rounded-md px-2 py-0.5 text-xs font-medium ${
            isRecibida
              ? (esTecnica ? "bg-[#9B2C2C]/15 text-[#9B2C2C] dark:text-[#F2B8B8]" : "bg-[#FE6A55]/15 text-[#FE6A55]")
              : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-200"}`}>
            {isRecibida ? "Sin tomar" : (ESTADO_LABEL[a.estado] || a.estado)}
          </span>
        </div>
      </div>

      {a.operador_nombre && !isRecibida && (
        <p className="mt-1.5 flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
          <UserCheck strokeWidth={1.6} className="h-3.5 w-3.5" /> {a.operador_nombre}
        </p>
      )}

      {cuidadora && (
        <div data-testid={`alarm-cuidadora-${a.id}`}
          className="mt-2.5 flex flex-wrap items-center gap-1.5 rounded-lg bg-[#012472]/8 dark:bg-[#80B3FF]/10 px-2.5 py-1.5 text-sm">
          <Phone strokeWidth={1.7} className="h-3.5 w-3.5 text-[#012472] dark:text-[#80B3FF]" />
          <span className="font-medium text-[#012472] dark:text-[#80B3FF]">Cuidadora en turno:</span>
          <span className="text-slate-700 dark:text-slate-200">{cuidadora.nombre}</span>
          <span className="tabular-nums text-slate-600 dark:text-slate-300">· {cuidadora.telefono}</span>
        </div>
      )}
    </button>
  );
}
